import { Component, useState, type ErrorInfo, type ReactNode } from 'react';

const DYNAMIC_IMPORT_ERROR =
  /ChunkLoadError|Loading (?:CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module|Importing a module script failed/i;

function createReference(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `err-${Date.now()}`;
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return '';
}

export function isDynamicImportError(error: unknown): boolean {
  return DYNAMIC_IMPORT_ERROR.test(errorMessage(error));
}

export interface ApplicationErrorScreenProps {
  appName?: string;
  error?: unknown;
  homeHref?: string;
  reference?: string;
  onRetry?: () => void;
}

export function ApplicationErrorScreen({
  appName = 'BeatQ Eats',
  error,
  homeHref = '/',
  reference,
  onRetry,
}: ApplicationErrorScreenProps) {
  const [generatedReference] = useState(createReference);
  const resolvedReference = reference ?? generatedReference;
  const staleVersion = isDynamicImportError(error);
  const reload = onRetry ?? (() => window.location.reload());

  return (
    <main
      role="alert"
      aria-labelledby="application-error-title"
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: 'clamp(24px, 6vw, 72px)',
        background:
          'radial-gradient(circle at top left, rgba(249, 115, 22, 0.13), transparent 38%), var(--color-surface-page, #fafaf9)',
        color: 'var(--color-text-default, #1c1917)',
        fontFamily: 'var(--font-family-sans, Inter, ui-sans-serif, system-ui, sans-serif)',
      }}
    >
      <section
        style={{
          width: 'min(100%, 560px)',
          padding: 'clamp(28px, 6vw, 48px)',
          border: '1px solid var(--color-border-default, #e7e5e4)',
          borderRadius: '20px',
          background: 'var(--color-surface-raised, #fff)',
          boxShadow: '0 24px 64px rgba(28, 25, 23, 0.1)',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: '52px',
            height: '52px',
            display: 'grid',
            placeItems: 'center',
            borderRadius: '16px',
            background: 'var(--color-action-primary, #c2410c)',
            color: '#fff',
            fontSize: '24px',
            fontWeight: 800,
            boxShadow: '0 10px 24px rgba(194, 65, 12, 0.24)',
          }}
        >
          !
        </div>

        <p
          style={{
            margin: '24px 0 8px',
            color: 'var(--color-action-primary, #c2410c)',
            fontSize: '13px',
            fontWeight: 750,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          {appName}
        </p>
        <h1
          id="application-error-title"
          style={{ margin: 0, fontSize: 'clamp(28px, 5vw, 40px)', lineHeight: 1.1 }}
        >
          We couldn&apos;t open this page
        </h1>
        <p
          style={{
            margin: '16px 0 0',
            color: 'var(--color-text-muted, #57534e)',
            fontSize: '16px',
            lineHeight: 1.6,
          }}
        >
          {staleVersion
            ? 'A newer version of the app may be available. Reload the page to continue.'
            : 'Something unexpected happened. Reload the page, or return home and try again.'}
        </p>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            marginTop: '28px',
          }}
        >
          <button
            type="button"
            onClick={reload}
            style={{
              minHeight: '44px',
              padding: '10px 18px',
              border: 0,
              borderRadius: '10px',
              background: 'var(--color-action-primary, #c2410c)',
              color: '#fff',
              cursor: 'pointer',
              font: 'inherit',
              fontWeight: 700,
            }}
          >
            Reload page
          </button>
          <a
            href={homeHref}
            style={{
              minHeight: '44px',
              display: 'inline-flex',
              alignItems: 'center',
              padding: '10px 18px',
              boxSizing: 'border-box',
              border: '1px solid var(--color-border-strong, #d6d3d1)',
              borderRadius: '10px',
              color: 'var(--color-text-default, #1c1917)',
              fontWeight: 700,
              textDecoration: 'none',
            }}
          >
            Return home
          </a>
        </div>

        <p
          style={{
            margin: '28px 0 0',
            color: 'var(--color-text-muted, #78716c)',
            fontSize: '12px',
            lineHeight: 1.5,
          }}
        >
          Reference: <code>{resolvedReference}</code>
        </p>
      </section>
    </main>
  );
}

interface ApplicationErrorBoundaryProps {
  appName?: string;
  children: ReactNode;
  homeHref?: string;
}

interface ApplicationErrorBoundaryState {
  error: Error | null;
  reference: string | null;
}

export class ApplicationErrorBoundary extends Component<
  ApplicationErrorBoundaryProps,
  ApplicationErrorBoundaryState
> {
  state: ApplicationErrorBoundaryState = { error: null, reference: null };

  static getDerivedStateFromError(error: Error): ApplicationErrorBoundaryState {
    return { error, reference: createReference() };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[application-error-boundary]', error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <ApplicationErrorScreen
          appName={this.props.appName}
          error={this.state.error}
          homeHref={this.props.homeHref}
          reference={this.state.reference ?? undefined}
        />
      );
    }
    return this.props.children;
  }
}
